import { IconButton, toast } from "@carbon6/c6-frontend-design";
import { useState, type ReactNode } from "react";
import type { DatasetFile } from "@/api";
import { DownloadIcon } from "@/components/icons";
import { HatTextModal } from "@/components/modal";
import { createLogger } from "@/logging";
import { triggerBrowserDownload } from "@/utils";
import { ExtraFileRenameDialog } from "../../ExtraFileRenameDialog";
import type { FileBrowserSource } from "../../fileBrowserSource";
import { resolveLoadErrorMessage } from "../../loadState";
import { FilePaneContent } from "./FilePaneContent";

const logger = createLogger("components/dataset/fileBrowser/file");

// 폴더 파일 목록에서 고른 파일 1건의 미리보기(우측). 카탈로그와 무관하게 표현이 같다. 다른
// 점은 둘뿐이다.
// - URL 은 `(kind, uuid)` 배치 서명 한 건으로 받는다.
// - 편집(이름 변경·삭제)은 `extra` 파일에만 연다. episode·meta 는 Dataset 원본이라 읽기 전용이다.
export function FilePane({
  source,
  datasetUuid,
  file,
  canModify = false,
  onChanged,
}: {
  source: FileBrowserSource;
  datasetUuid: string;
  file: DatasetFile;
  canModify?: boolean;
  // 이름 변경이나 삭제가 성공하면 호출한다. 부모는 디렉터리 트리와 파일 목록을 다시 조회한다.
  onChanged?: () => void;
}): ReactNode {
  // extra 파일이고 편집이 열려 있을 때만 편집을 노출한다.
  const editable = file.kind === "extra" && canModify;
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [renameError, setRenameError] = useState<string | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // 배치 서명은 행 단위로 degrade 한다. 한 건짜리 요청이므로 그 행이 결과다. 받을 수 없는
  // 파일(사본 미배치·orphan)은 사유가 문자열로 온다. 미리보기 영역은 그 사유를 안내로 표시한다.
  async function resolveUrl(): Promise<string> {
    const [signed] = await source.signFiles(datasetUuid, [{ kind: file.kind, uuid: file.uuid }]);
    if (signed === undefined || signed.url === null) {
      throw new Error(
        `이 파일은 지금 받을 수 없습니다. (${signed?.unavailable_reason ?? "unknown"})`,
      );
    }
    return signed.url;
  }

  async function download() {
    try {
      // 1회용 signed GET URL 은 storage origin 이므로 새 탭으로 연다. registry 는 바이트를 프록시하지 않는다.
      const url = await resolveUrl();
      triggerBrowserDownload(url, { filename: file.filename, crossOrigin: true });
    } catch (error) {
      logger.error("file url (download) failed", error);
      toast.error("다운로드 링크를 가져오지 못했습니다. 잠시 후 다시 시도해 주세요.");
    }
  }

  async function submitRename(relativePath: string) {
    if (!editable || !source.rename) return;
    setRenameSubmitting(true);
    setRenameError(null);
    try {
      await source.rename(datasetUuid, file.uuid, { relative_path: relativePath });
      toast.success("파일 이름을 변경했습니다.");
      setRenameOpen(false);
      onChanged?.();
    } catch (error) {
      logger.error("rename extra file failed", error);
      setRenameError(resolveLoadErrorMessage(error));
    } finally {
      setRenameSubmitting(false);
    }
  }

  async function confirmDelete() {
    if (!editable || !source.remove) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      await source.remove(datasetUuid, file.uuid);
      toast.success("파일을 삭제했습니다.");
      setDeleteOpen(false);
      onChanged?.();
    } catch (error) {
      logger.error("delete extra file failed", error);
      setDeleteError(resolveLoadErrorMessage(error));
    } finally {
      setDeleteSubmitting(false);
    }
  }

  return (
    <FilePaneContent
      fileUuid={file.uuid}
      name={file.filename}
      format={file.file_format}
      sizeBytes={file.size_bytes}
      path={file.relative_path}
      resolveUrl={resolveUrl}
      actions={
        <>
          <IconButton text="다운로드" size="md" variant="solid" onClick={() => void download()}>
            <DownloadIcon />
          </IconButton>
          {editable && source.rename ? (
            <IconButton
              text="이름 변경"
              size="md"
              variant="surface"
              onClick={() => setRenameOpen(true)}
            />
          ) : null}
          {editable && source.remove ? (
            <IconButton
              text="삭제"
              size="md"
              variant="surface"
              tone="danger"
              onClick={() => setDeleteOpen(true)}
            />
          ) : null}
        </>
      }
    >
      {renameOpen ? (
        <ExtraFileRenameDialog
          file={file}
          submitting={renameSubmitting}
          errorMessage={renameError}
          onClose={() => {
            if (!renameSubmitting) {
              setRenameOpen(false);
              setRenameError(null);
            }
          }}
          onSubmit={(relativePath) => void submitRename(relativePath)}
        />
      ) : null}

      <HatTextModal
        open={deleteOpen}
        label="추가 파일을 삭제합니다."
        eyebrow={file.filename}
        caption="이 추가 파일을 영구적으로 삭제합니다."
        error
        okText="삭제"
        busy={deleteSubmitting}
        errorMessage={deleteError ?? undefined}
        onOkPress={() => void confirmDelete()}
        onCancelPress={() => {
          if (!deleteSubmitting) {
            setDeleteOpen(false);
            setDeleteError(null);
          }
        }}
      />
    </FilePaneContent>
  );
}
